import { useLayoutEffect, useState, type RefObject } from 'react';

/** Content-box size of an element, in CSS pixels. */
export interface ElementSize {
  width: number;
  height: number;
}

const ZERO: ElementSize = { width: 0, height: 0 };

function px(value: string): number {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** The content box, measured now: the client box minus padding. */
function measureContentBox(element: HTMLElement): ElementSize {
  const style = window.getComputedStyle(element);
  return {
    width: Math.max(0, element.clientWidth - px(style.paddingLeft) - px(style.paddingRight)),
    height: Math.max(0, element.clientHeight - px(style.paddingTop) - px(style.paddingBottom))
  };
}

/**
 * Follows the content-box size of `ref`'s element with a `ResizeObserver`.
 *
 * It is measured once synchronously before the first paint, so a layout
 * sized from it does not flash at zero size, and then again whenever the
 * element changes size: a window resize, the chat drawer opening or closing
 * beside it, a phone rotating.
 *
 * Returns 0 × 0 until there is an element to measure.
 */
export function useElementSize<T extends HTMLElement>(ref: RefObject<T | null>): ElementSize {
  const [size, setSize] = useState<ElementSize>(ZERO);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = (next: ElementSize) => {
      setSize((current) =>
        current.width === next.width && current.height === next.height ? current : next
      );
    };

    update(measureContentBox(element));

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      update({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}
